<template>
  <tr
    class="bg-white hover:bg-gray-50 transition-colors duration-150 ease-in-out text-right child:py-5 first-child:pl-3 last-child:pr-3"
    @click="open = !open"
  >
    <td class="flex items-center space-x-1">
      <button
        data-test="open-claim"
        type="button"
        class="btn-icon btn-icon-default"
        :aria-label="$t('ProgressClaims.TradeRow.accessibility.toggleDetails')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
          :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
      </button>

      <div class="flex items-start space-x-2 text-left">
        <span
          class="font-semibold text-gray-900"
          :aria-label="$t('ProgressClaims.TradeRow.accessibility.billNumber')"
        >{{ trade.billNumber }}</span>

        <p
          class="w-60"
          :aria-label="$t('ProgressClaims.TradeRow.accessibility.tradeName')"
        >
          {{ trade.name }}
        </p>
      </div>
    </td>

    <td
      class="group pr-3"
      :title="showBudgetCommentTooltip ? varianceComment.body : ''"
      :aria-label="$t('ProgressClaims.TradeRow.accessibility.budgetAmount')"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showBudgetCommentButton"
          type="button"
          class="btn-icon group-hover:visible btn-icon-default invisible mr-1 shrink-0"
          aria-label="Edit comment"
          @click.stop="editBudgetComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          <div v-if="totalCosts.budget !== 0">
            <span
              v-if="totalCosts.budget - totalCosts.adjustedBudget !== 0"
              class="text-gray-600"
            >Works:</span> {{ australianCurrency(totalCosts.budget) }}
          </div>

          <div v-if="totalCosts.budget - totalCosts.adjustedBudget !== 0">
            <span
              v-if="totalCosts.budget !== 0"
              class="text-gray-600"
            >Adjustments:</span> {{ australianCurrency(-(totalCosts.budget - totalCosts.adjustedBudget)) }}
          </div>
        </div>
      </div>
    </td>

    <td
      class="bg-green-200/50"
      :aria-label="$t('ProgressClaims.TradeRow.accessibility.currentClaim')"
    >
      {{ totalCosts.budget > 0 ? australianCurrency(totalCosts.currentClaim) : '' }}
    </td>

    <td
      class="bg-green-200/50"
      :aria-label="$t('ProgressClaims.TradeRow.accessibility.currentCertified')"
    >
      {{ totalCosts.budget > 0 ? australianCurrency(totalCosts.currentCertified) : '' }}
    </td>

    <td
      class="bg-green-200/50"
      :aria-label="$t('ProgressClaims.TradeRow.accessibility.previousAmount')"
    >
      {{ totalCosts.budget > 0 ? australianCurrency(totalCosts.previous) : '' }}
    </td>

    <td
      class="bg-green-200/50 pr-3"
      :aria-label="$t('ProgressClaims.TradeRow.accessibility.cumulativeAmount')"
    >
      {{ totalCosts.budget > 0 ? australianCurrency(totalCosts.cumulative) : '' }}
    </td>

    <td
      class="bg-blue-100/50"
      :aria-label="$t('ProgressClaims.TradeRow.accessibility.claimPercentage')"
    >
      <span v-if="showPercentages">
        {{ totalCosts.budget > 0 ? (isNumber(totalCosts.currentClaim / totalCosts.budget) ? $tp((totalCosts.currentClaim / totalCosts.budget) * 100) : 0) : '' }}
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
      :aria-label="$t('ProgressClaims.TradeRow.accessibility.certifiedPercentage')"
    >
      {{ totalCosts.budget > 0 ? (isNumber(totalCosts.currentCertified / totalCosts.budget) ? $tp((totalCosts.currentCertified / totalCosts.budget) * 100) : 0) : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
      :aria-label="$t('ProgressClaims.TradeRow.accessibility.previousPercentage')"
    >
      {{ totalCosts.budget > 0 ? (isNumber(totalCosts.previous / totalCosts.budget) ? $tp((totalCosts.previous / totalCosts.budget) * 100) : 0) : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
      :aria-label="$t('ProgressClaims.TradeRow.accessibility.cumulativePercentage')"
    >
      {{ totalCosts.budget > 0 ? (isNumber(totalCosts.cumulative / totalCosts.budget) ? $tp((totalCosts.cumulative / totalCosts.budget) * 100) : 0) : '' }}
    </td>

    <td
      class="group"
      :class="{ 'text-red-600': hasVariance && variance !== 0 }"
      :title="hasVariance && variance !== 0 && varianceComment?.body ? varianceComment.body : ''"
      :aria-label="$t('ProgressClaims.TradeRow.accessibility.varianceAmount')"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showCommentButton"
          type="button"
          class="btn-icon group-hover:visible btn-icon-default invisible mr-1 shrink-0"
          aria-label="Edit variance comment"
          @click.stop="editVarianceComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          {{ totalCosts.budget > 0 ? australianCurrency(totalCosts.currentClaim - totalCosts.currentCertified) : '' }}
        </div>
      </div>
    </td>

    <td
      class="pr-3"
      :aria-label="$t('ProgressClaims.TradeRow.accessibility.remainingBudget')"
    >
      {{ totalCosts.budget > 0 ? australianCurrency(Math.abs(totalCosts.budget - totalCosts.cumulative)) : '' }}
    </td>
  </tr>

  <template v-if="open">
    <TradeRowItem
      v-for="tradeItem in tradeItems"
      :key="tradeItem.id"
      :trade="$props.trade"
      :claim="$props.claim"
      :trade-item="tradeItem"
      :show-percentages="$props.showPercentages"
    />
  </template>
</template>

<script setup lang="ts">
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber, falsey, isNumber } from '@/helpers';
import { t } from '@/i18n';
import Claim, { ClaimStatus } from '@/models/Claim';
import Trade from '@/models/Trade';
import { useCommentsStore } from '@/store/comments';
import { useTradeItemsStore } from '@/store/tradeItems';
import { useTradesStore } from '@/store/trades';
import Swal from 'sweetalert2';
import { computed, ref } from 'vue';
import { toast } from 'vue3-toastify';
import TradeRowItem from './TradeRowItem.vue';

const props = defineProps<{
  trade: Trade;
  claim: Claim;
  showPercentages?: boolean;
}>();

const tradeItemsStore = useTradeItemsStore();

const tradeItems = computed(() => {
  return tradeItemsStore.models
    .where('trade_id', props.trade.id)
    .whereNull('parent_id')
    .with('trade_item_costs', (query) => {
      query.where('claim_id', props.claim.id);
    }).orderBy('sort_order')
    .get();
});

const totalCosts = computed(() => {
  const totals = {
    budget: 0,
    adjustedBudget: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
    variance: 0,
  };

  tradeItems.value.forEach((tradeItem) => {
    totals.budget += tradeItem.childBudget();
    totals.adjustedBudget += tradeItem.childAdjustedBudget();
    totals.previous += tradeItem.childPrevious(props.claim);
    totals.currentClaim += tradeItem.childClaim(props.claim);
    totals.currentCertified += tradeItem.childCertified(props.claim);
    totals.variance += tradeItem.childVariance(props.claim);
  });

  totals.cumulative = totals.previous + totals.currentCertified;

  return totals;
});

const open = ref(false);
const commentsStore = useCommentsStore();
const tradesStore = useTradesStore();
const updateCommentAction = useStoreApiAction(tradesStore.updateComment);

const canEditCertified = computed(() => {
  return props.claim.status === ClaimStatus.draft;
});

const varianceComment = computed(() => {
  return commentsStore.models.where('commentable_id', props.trade.id).where('commentable_type', props.trade.$entity())
    .where('creator_id', props.claim.id).where('creator_type', props.claim.$entity())
    .first();
});

const hasVariance = computed(() => {
  return !falsey(totalCosts.value?.currentClaim) && !falsey(totalCosts.value?.currentCertified);
});

const variance = computed(() => {
  return totalCosts.value?.currentClaim - totalCosts.value?.currentCertified;
});

const showCommentButton = computed(() => {
  return hasVariance.value && variance.value !== 0 && !!varianceComment.value && canEditCertified.value;
});

const showBudgetCommentButton = computed(() => {
  return canEditCertified.value && !!varianceComment.value && (!hasVariance.value || variance.value === 0);
});

const showBudgetCommentTooltip = computed(() => {
  return !!varianceComment.value?.body && showBudgetCommentButton.value;
});

const editBudgetComment = () => {
  editVarianceComment();
};

const editVarianceComment = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentTitle'),
    text: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentText'),
    input: 'textarea',
    inputValue: varianceComment.value?.body ?? '',
    showCancelButton: true,
    confirmButtonText: 'Update',
  }).then((result) => {
    if(result.isConfirmed) {
      if(result.value) {
        updateCommentAction.request(props.trade.id, varianceComment.value.id, { comment: result.value }).then(() => {
          toast.success('Comment updated');
        });
      }
    }
  });
};
</script>

<style scoped></style>
